import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Card, Descriptions, Spin, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatTime, supabase } from '@ubus/shared';
import dayjs from 'dayjs';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

const { Title, Text, Paragraph } = Typography;

type ShareTrip = {
  trip_number?: string;
  date?: string;
  pickup_time?: string;
  pickup_address?: string;
  dropoff_address?: string;
  passenger_count?: number;
  status?: string;
  cancelled_reason?: string | null;
  estimated_dropoff_time?: string | null;
  vehicle_fleet_number?: string | null;
  vehicle_plate?: string | null;
  driver_name?: string | null;
  driver_phone?: string | null;
};

type RpcResult =
  | { ok: true; trip: ShareTrip }
  | { ok: false; error?: string };

function statusColor(status?: string): string {
  switch (status) {
    case 'dispatched':
      return 'cyan';
    case 'in_progress':
      return 'blue';
    case 'completed':
      return 'green';
    case 'cancelled':
      return 'red';
    default:
      return 'default';
  }
}

export default function TripSharePage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trip, setTrip] = useState<ShareTrip | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) {
        setError('invalid_token');
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const { data, error: rpcError } = await supabase.rpc('get_trip_share_by_token', {
          p_token: token,
        });
        if (rpcError) throw rpcError;
        const result = data as RpcResult;
        if (!result || result.ok !== true) {
          setError((result && 'error' in result && result.error) || 'not_found');
          setTrip(null);
        } else {
          setTrip(result.trip);
          setError(null);
        }
      } catch {
        setError('not_found');
        setTrip(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f1f5f9',
        padding: '24px 16px 48px',
      }}
    >
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            UBUS
          </Title>
          <LanguageSwitcher size="small" />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : error === 'expired' ? (
          <Alert
            type="warning"
            showIcon
            message={t('tripShare.expiredTitle')}
            description={t('tripShare.expiredBody')}
          />
        ) : error || !trip ? (
          <Alert
            type="error"
            showIcon
            message={t('tripShare.notFoundTitle')}
            description={t('tripShare.notFoundBody')}
          />
        ) : (
          <Card style={{ borderRadius: 12 }}>
            <Title level={4} style={{ marginTop: 0 }}>
              {t('tripShare.title')}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              {trip.trip_number ? (
                <>
                  {t('tripShare.tripRef')}: <Text code>{trip.trip_number}</Text>
                </>
              ) : null}
            </Paragraph>
            <Tag color={statusColor(trip.status)} style={{ marginBottom: 16 }}>
              {t(`tripShare.status.${trip.status ?? 'scheduled'}`, {
                defaultValue: trip.status ?? '',
              })}
            </Tag>
            {trip.status === 'cancelled' && trip.cancelled_reason ? (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('tripShare.cancelled')}
                description={trip.cancelled_reason}
              />
            ) : null}
            <Descriptions column={1} bordered size="middle">
              <Descriptions.Item label={t('common.date')}>
                {trip.date ? dayjs(trip.date).format('ddd, DD MMM YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('tripShare.pickupTime')}>
                {trip.pickup_time ? formatTime(String(trip.pickup_time)) : '—'}
              </Descriptions.Item>
              {trip.estimated_dropoff_time ? (
                <Descriptions.Item label={t('tripShare.dropoffTime')}>
                  {formatTime(String(trip.estimated_dropoff_time))}
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label={t('common.pickup')}>
                {trip.pickup_address || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.dropoff')}>
                {trip.dropoff_address || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('tripShare.vehicle')}>
                {[trip.vehicle_fleet_number, trip.vehicle_plate].filter(Boolean).join(' · ') ||
                  t('common.pending')}
              </Descriptions.Item>
              <Descriptions.Item label={t('tripShare.driver')}>
                {trip.driver_name || t('common.pending')}
              </Descriptions.Item>
              <Descriptions.Item label={t('tripShare.driverPhone')}>
                {trip.driver_phone || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.passengers')}>
                {trip.passenger_count ?? '—'}
              </Descriptions.Item>
            </Descriptions>
            <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0, fontSize: 12 }}>
              {t('tripShare.footer')}
            </Paragraph>
          </Card>
        )}
      </div>
    </div>
  );
}
